import { memo, useCallback, useMemo, useRef } from 'react';
import { SingularStationArea } from '@thive/fddm-types';
import { useTranslation } from 'react-i18next';

import { SelectInput } from '@common/components/inputs';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { usePropertyPanelConfirm } from '@/modules/common/components/usePropertyPanelConfirm';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { isSingularStationArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';

const SINGULAR_STATION_LOAD_HANDLING_TYPE_OPTIONS: {
  label: string;
  value: Required<SingularStationArea['loadHandlingType']>;
}[] = [
  { label: 'Intake', value: 'INTAKE' },
  { label: 'Delivery', value: 'DELIVERY' },
  { label: 'Storage', value: 'STORAGE' },
  { label: 'Off vehicle processing', value: 'OFF_VEHICLE_PROCESSING' },
  { label: 'On vehicle processing', value: 'ON_VEHICLE_PROCESSING' },
];

const getSelectedSingularStationArea = (
  selectedAreas: ReturnType<typeof useReadSelectedAreas>,
): SingularStationArea | null => {
  const areas = areasToFlatCollection(selectedAreas);
  if (areas.length !== 1) return null;
  const area = areas[0];
  if (!isSingularStationArea(area)) return null;
  return area;
};

function SingularStationLoadHandlingTypeComponent() {
  const { t } = useTranslation('interface');
  const selectedAreas = useReadSelectedAreas();
  const { confirm, Dialog } = usePropertyPanelConfirm();
  const ref = useRef<HTMLDivElement>(null);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);

  const area = useMemo(() => getSelectedSingularStationArea(selectedAreas), [selectedAreas]);

  const onChange = useCallback(
    async (newValue: SingularStationArea['loadHandlingType']) => {
      if (!area) return;

      const flows = fddmSvc.getFlowsByAreaId(area.id) ?? [];
      const flowStop = fddmSvc.getFlowStopByAreaId(area.id);
      const hasRelatedFlowData = flows.length > 0 || Boolean(flowStop);
      const resetCanBeUsedForParking = newValue !== 'INTAKE' && area.loadHandlingType === 'INTAKE';

      if (hasRelatedFlowData) {
        const confirmed = await confirm(
          t('interface:properties.flows.change_load_handling_type.label'),
          t('interface:properties.flows.change_load_handling_type.message'),
        );

        if (!confirmed) return;
      }

      historySvc.batch(() => {
        if (flowStop) {
          fddmSvc.deleteFlowStops([flowStop]);
        }

        if (flows.length > 0) {
          fddmSvc.deleteFlows(flows);
        }

        fddmSvc.updateArea({
          ...area,
          loadHandlingType: newValue,
          loadHandlingPoiSpecs: area.loadHandlingPoiSpecs.map((loadHandlingPoiSpec) => ({
            ...loadHandlingPoiSpec,
            canBeUsedForParking: resetCanBeUsedForParking
              ? false
              : loadHandlingPoiSpec.canBeUsedForParking,
            parkingExtension: resetCanBeUsedForParking
              ? undefined
              : loadHandlingPoiSpec.parkingExtension,
          })),
        });
      });
    },
    [area, confirm, fddmSvc, historySvc, t],
  );

  if (!area) return null;

  return (
    <>
      <Dialog anchor={ref} offset={[0, 100]} />
      <div ref={ref}>
        <PropertiesLabel i18nkey='interface:properties.area.load_handling_type'>
          <SelectInput
            options={SINGULAR_STATION_LOAD_HANDLING_TYPE_OPTIONS}
            onChange={onChange}
            value={area.loadHandlingType}
          />
        </PropertiesLabel>
      </div>
    </>
  );
}

export const LoadHandlingType = memo(SingularStationLoadHandlingTypeComponent);
